// surface-topbar-chrome — the activity top bar's Back (one-tab-topbar, Trello 3h9FqNWJ).
//
// The SIXTEENTH sibling chrome module. NOT uniformly wired: it allow-lists the surface to
// exactly `assignment | challenge | viewonly | tweak | predict | debug | completion | guided-make
// | find-the-bug | compare` (module-local `ALLOWED_SURFACES`; one-tab-same-tab-launches, Trello
// bD1oe35m, added the seven tutor surfaces) and is a no-op without the server-rendered
// `#surface-topbar-back` (lib/views/includes/surface-topbar.html, rendered by
// clientside-base.html only when a handler sets `surfaceTopbar`) — so predict's teacher CAPTURE
// variant, which never sets it, stays inert. The four run pages share `viewonly.html` and get the
// bar from their handlers. Only assignment, challenge, guided-make and find-the-bug autosave a
// Draft, so only they pass save functions; every other surface passes `{ surface }` alone.
//
// Wired EAGERLY from main.js (beside plan-chrome, above the `crossOriginIsolated` early
// return): its inputs are the server markup and <body> attributes only, and Back must still
// work when the page cannot isolate or `loadProject()` rejects. The surface's save functions are
// PASSED IN by main.js (the `wireVersions` pattern), late-bound, so this module imports only
// `close-prompt.js` — never the surface chromes.
//
//   target  — close-prompt.js's exported `readReturnTarget()` (the countdown's own function,
//             through `safeReturnTarget`), so Back and the countdown always land on the same
//             place. With one: href = target, "Back" / "Back to the lesson" (preview: "Back to
//             class" both). Without: "My classes" with the house icon, `/home`. NEVER the
//             browser history.
//   click   — a plain primary click only (a modified or middle click keeps link behaviour):
//             busy ("Saving…", spinner, aria-busy, "Saving your work…") → wait for an in-flight
//             assignment Submit → flush ONLY when `hasPendingSave()` reports unsaved work →
//             navigate (`location.replace` in preview, the strip's old exit, so browser Back does
//             not walk into preview; an ordinary navigation otherwise). A press while busy is
//             ignored, so there is exactly one navigation.
//   failure — the flush rejects or resolves an object whose `success !== true` (`null` means
//             nothing to save = success): Back is restored, focus stays on it, and the
//             `role="alert"` row under the bar explains, with a "Try again" button that re-runs
//             Back. The row clears on the next Back press.
//   bfcache — a `pageshow` with `persisted` resets the busy state, the label and the alert.

import { readReturnTarget } from './close-prompt.js';

export var ALLOWED_SURFACES = ['assignment', 'challenge', 'viewonly', 'tweak', 'predict', 'debug',
  'completion', 'guided-make', 'find-the-bug', 'compare'];

var SVG_NS = 'http://www.w3.org/2000/svg';

// one-tab-clarify-wording: the save-failed row's second and third sentences. "press Try again"
// is true whether Back reads "Back" or "My classes" (the button re-runs Back).
var SAVE_FAILED_REST = ' Check your internet, then press Try again. Your work is still here.';

function isPlainPrimaryClick(e) {
  if (!e) { return false; }
  if (e.defaultPrevented) { return false; }
  if (typeof e.button === 'number' && e.button !== 0) { return false; }
  return !(e.ctrlKey || e.metaKey || e.shiftKey || e.altKey);
}

function alertIcon(doc) {
  if (!doc.createElementNS) { return null; }
  try {
    var svg = doc.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'pcab-ico');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    [['circle', { cx: '12', cy: '12', r: '10' }],
      ['line', { x1: '12', x2: '12', y1: '8', y2: '12' }],
      ['line', { x1: '12', x2: '12.01', y1: '16', y2: '16' }]].forEach(function (spec) {
      var n = doc.createElementNS(SVG_NS, spec[0]);
      Object.keys(spec[1]).forEach(function (k) { n.setAttribute(k, spec[1][k]); });
      svg.appendChild(n);
    });
    return svg;
  } catch (e) { return null; }
}

/**
 * @param {Document} doc
 * @param {{ surface?: string, hasPendingSave?: Function, flushPendingSave?: Function,
 *           whenSubmitSettled?: Function }} [opts]
 * @returns {{ go: Function } | null} a handle (specs), or null when inert. Never throws.
 */
export function wireSurfaceTopbar(doc, opts) {
  try {
    var o = opts || {};
    if (!doc || !doc.body) { return null; }
    var surface = o.surface || doc.body.getAttribute('data-surface') || '';
    if (ALLOWED_SURFACES.indexOf(surface) === -1) { return null; }
    var back = doc.getElementById('surface-topbar-back');
    if (!back) { return null; }

    var alertRow = doc.getElementById('surface-topbar-alert');
    var labelEl = back.querySelector ? back.querySelector('.pcab-back-label') : null;
    var preview = doc.body.getAttribute('data-preview') === 'true';
    var target = readReturnTarget();

    var dest, text, aria, homeIcon;
    if (target) {
      dest = target;
      text = preview ? 'Back to class' : 'Back';
      aria = preview ? 'Back to class' : 'Back to the lesson';
      homeIcon = false;
    } else {
      dest = '/home';
      text = 'My classes';
      aria = 'Back to my classes';
      homeIcon = true;
    }

    function setLabel(t) { if (labelEl) { labelEl.textContent = t; } }

    function applyNormal() {
      back.setAttribute('href', dest);
      back.setAttribute('aria-label', aria);
      back.removeAttribute('aria-busy');
      if (homeIcon) { back.setAttribute('data-icon', 'home'); } else { back.removeAttribute('data-icon'); }
      setLabel(text);
    }

    function applyBusy() {
      back.setAttribute('aria-busy', 'true');
      back.setAttribute('aria-label', 'Saving your work…');
      setLabel('Saving…');
    }

    function clearAlert() {
      if (!alertRow) { return; }
      alertRow.hidden = true;
      alertRow.textContent = '';
    }

    function showAlert() {
      if (!alertRow) { return; }
      alertRow.textContent = '';
      var icon = alertIcon(doc);
      if (icon) { alertRow.appendChild(icon); }
      var msg = doc.createElement('span');
      msg.className = 'pcab-alert-text';
      var strong = doc.createElement('b');
      strong.textContent = 'We couldn’t save your work.';
      msg.appendChild(strong);
      msg.appendChild(doc.createTextNode
        ? doc.createTextNode(SAVE_FAILED_REST)
        : { nodeType: 3, textContent: SAVE_FAILED_REST });
      alertRow.appendChild(msg);
      var retry = doc.createElement('button');
      retry.type = 'button';
      retry.className = 'pcab-retry';
      retry.textContent = 'Try again';
      retry.addEventListener('click', function () { go(); });
      alertRow.appendChild(retry);
      alertRow.hidden = false;
    }

    function navigate() {
      if (preview) { window.location.replace(dest); } else { window.location.href = dest; }
    }

    var busy = false;

    function fail(err) {
      busy = false;
      applyNormal();
      showAlert();
      try { back.focus(); } catch (e) { /* focus is best-effort */ }
      if (err) { console.error(err); }
    }

    function go() {
      if (busy) { return null; }
      busy = true;
      clearAlert();
      applyBusy();
      return Promise.resolve()
        .then(function () {
          return typeof o.whenSubmitSettled === 'function' ? o.whenSubmitSettled() : null;
        })
        .then(function () {
          var pending;
          try { pending = typeof o.hasPendingSave === 'function' && o.hasPendingSave() === true; }
          catch (e) { pending = false; }
          if (!pending || typeof o.flushPendingSave !== 'function') { return null; }
          return o.flushPendingSave();
        })
        .then(function (res) {
          if (res !== null && res !== undefined && !(typeof res === 'object' && res.success === true)) {
            fail(null);
            return;
          }
          // `busy` stays true: the page is leaving, and a second press must not navigate twice.
          navigate();
        }, fail);
    }

    back.addEventListener('click', function (e) {
      if (!isPlainPrimaryClick(e)) { return; }
      if (e.preventDefault) { e.preventDefault(); }
      go();
    });

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('pageshow', function (e) {
        if (!e || e.persisted !== true) { return; }
        busy = false;
        applyNormal();
        clearAlert();
      });
    }

    applyNormal();
    return { go: go };
  } catch (err) {
    try { console.error(err); } catch (e) { /* never throw */ }
    return null;
  }
}
